import { useMemo, useState } from "react"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import { renderRowActionMenuItems } from "../components/tableElements/renderRowActionMenuItems"
import { ProductGroupSelector } from "../components/tableElements/ProductGroupSelector"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { PriceListSelector } from "../components/tableElements/PriceListSelector"
import { ProductGroupItem } from "../../productGroup/productGroupTypes"
import { MerchantProductsQuery, fetchProducts } from "../productsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { SelectShop } from "../../shops/components/SelectShop"
import { PriceListType } from "../../priceList/priceListApi"
import {
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
    defaultPreviewProductImageState,
} from "../components/tableElements/PreviewProductImage"
import {
    DefaultConfirmModalStateType,
    DeleteProductDialog,
    defaultConfirmModalState,
} from "../components/tableElements/DeleteProductDialog"
import { transformProductsData } from "../helpers"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { MRT_ColumnDef } from "material-react-table"
import { ProductTableRow } from "../productsTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { ProductGroupItemBox, ProductName } from "../ProductTableColumns"

const useStyles = makeStyles()(() => ({
    hideRowActions: {
        "& .MuiIconButton-root": {
            display: "none",
        },
    },
}))

type ProductGroupPageProps = Readonly<{}>

export function AllProductsTab({}: ProductGroupPageProps) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)
    const [priceListItem, setPriceListItem] = useState<PriceListType | null>(null)
    const [productGroupId, setProductGroupId] = useState<ProductGroupItem | null>(null)
    const [shopId, setShopId] = useState<string>()

    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            shopId,
            ...(priceListItem && { priceListId: priceListItem.id }),
            ...(productGroupId && { productGroupId: productGroupId.id }),
        },
        select: transformProductsData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const columns = useMemo<MRT_ColumnDef<ProductTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                header: l10n.getString("products-name-column"),
                Cell: ({ row }) => {
                    if (row.original.type === "group") {
                        return <ProductGroupItemBox productGroup={row.original} />
                    }

                    if (row.original.type === "product") {
                        return (
                            <ProductName
                                product={row.original}
                                onProductPhotoClick={(imageUrl) => {
                                    setPreviewImage({ show: true, image: imageUrl })
                                }}
                            />
                        )
                    }

                    return ""
                },
            }),
            columnFactory.moneyColumn(
                {
                    id: "price",
                    header: l10n.getString("products-price-column"),
                },
                (row) => (row.type === "group" ? undefined : row.price),
                true,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("products-status-column"),
                },
                (row) => row.isActive ?? false,
                {
                    cellOverride: (row) => (row.type === "group" ? "" : null),
                },
            ),
        ],
        [l10n, columnFactory, setPreviewImage],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <ProductGroupSelector
                            productGroupId={productGroupId}
                            handleChange={(el) => setProductGroupId(el)}
                        />
                        <SelectShop shopId={shopId} setShopId={setShopId} />
                        <PriceListSelector priceListItem={priceListItem} handleChange={(el) => setPriceListItem(el)} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderRowActionMenuItems={({ row, closeMenu }) =>
                    renderRowActionMenuItems({ row, closeMenu, setConfirmModalState, navigate })
                }
                muiTableBodyRowProps={({ row }) => ({
                    className: row.original.type === "group" ? classes.hideRowActions : "",
                })}
            />

            {/* Confirm delete */}
            {confirmModalState.showConfirmModal && (
                <DeleteProductDialog
                    confirmModalState={confirmModalState}
                    setConfirmModalState={setConfirmModalState}
                />
            )}

            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}
        </Grid>
    )
}
